import { inject, Injectable } from '@angular/core';
import { ActionDisabledCondition, ActionItemOptions, UserAction, UserStoreService } from '@mediahaven/shared';
import {
    ApiFragment,
    DeleteStatus,
    Mh2Entity,
    NavigationLink,
    UserFunction,
    UserFunctions
} from '@stylehaven/components';
import { Observable } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ActionService } from '../../abstract/action.service.interface';
import { MediaHavenActionType } from '@common/sandboxes/actions/entities/action-types.entity';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';

@Injectable({ providedIn: 'root' })
export class PermanentDeleteActionService extends ActionService {
    protected override readonly action: MediaHavenActionType = MediaHavenActionType.PERMANENT_DELETE;

    // Services
    private readonly dialog: DialogHelper = inject(DialogHelper);
    private readonly userStoreService: UserStoreService = inject(UserStoreService);
    private readonly recordSandbox: RecordsSandbox = inject(RecordsSandbox);

    // Behaviour
    protected readonly ENABLE_BUSY_TOAST: boolean = false;
    protected readonly REFRESH_ON_ACTION: boolean = true;
    protected readonly DISABLE_ON_EXECUTE: boolean = true;

    /* Permanent Delete
     * Available when:
     *  * User has delete rights
     *  * User has permanent delete rights
     *  * Item is deleted ('LogicallyDeleted')
     */
    public getUserAction(record: ApiFragment): Observable<UserAction> {
        return this.getUserFunctions().pipe(map(userFunctions => this.userActionBuilder(record, userFunctions)));
    }

    public userActionBuilder(record: ApiFragment, userFunctions: UserFunctions): UserAction {
        const hasDeleteRights: boolean = record.hasDeleteRights();
        const hasPermanentDeleteRights: boolean = userFunctions[UserFunction.recycleRecords];
        const isLogicallyDeleted: boolean = record.getDeleteStatus() === DeleteStatus.LogicallyDeleted;

        const availableConditions: boolean[] = [hasDeleteRights, hasPermanentDeleteRights, isLogicallyDeleted];
        const disableConditions: ActionDisabledCondition[] = [];

        return new UserAction(availableConditions, disableConditions);
    }

    private getUserFunctions(): Observable<UserFunctions> {
        return this.userStoreService.getUserFunctions();
    }

    protected onAction(record: ApiFragment, actionItemOptions: ActionItemOptions): Observable<boolean> {
        const message: string = this.getPermanentDeleteConfirmationMessage(record);
        const title: string = this.translate('actions.permanently-delete');
        const yes: string = this.translate('actions.permanently-delete');
        const no: string = this.translate('actions.common.cancel');

        return this.dialog.confirm({ title, message, yes, no }).pipe(
            tap(() => this.initBusyToast(record, actionItemOptions, true)),
            switchMap(confirm => this.recordSandbox.permanentlyDeleteRecord(record).pipe(take(1)))
        );
    }

    private getPermanentDeleteConfirmationMessage(record: ApiFragment): string {
        const entity: Mh2Entity = record.getMh2Entity();
        return this.translate(`items.permanently-delete-confirm-dialog.${entity}`, { items: 1 });
    }

    // #region Labels

    public override getLabelKey(target: ApiFragment): string {
        return 'actions.permanently-delete';
    }

    public override getToastLabelKey(target: ApiFragment): string {
        return 'action-toasts.permanently-delete';
    }

    public override getToastEntity(target: ApiFragment, actionItemOptions: ActionItemOptions): Mh2Entity | string {
        return target.getMh2Entity();
    }

    public override getSuccessToastLinkLabelKey(target: ApiFragment, actionItemOptions: ActionItemOptions): string {
        return '';
    }

    public override getSuccessToastLink(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): NavigationLink | undefined {
        return undefined;
    }

    public override getToastTranslationOptions(
        target: ApiFragment,
        actionItemOptions: ActionItemOptions
    ): Record<string, string> {
        return {};
    }

    // #endregion
}
